import { openDB } from 'idb';

const DB_NAME = 'soil-testing-offline-db';
const STORE_NAME = 'offline-queue';
const CACHE_STORE_NAME = 'api-cache';
const OVERRIDE_STORE_NAME = 'outcome-overrides';

class OfflineManager {
    constructor() {
        this.dbPromise = openDB(DB_NAME, 2, {
            upgrade(db) {
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
                }
                if (!db.objectStoreNames.contains(CACHE_STORE_NAME)) {
                    db.createObjectStore(CACHE_STORE_NAME, { keyPath: 'url' });
                }
                if (!db.objectStoreNames.contains(OVERRIDE_STORE_NAME)) {
                    db.createObjectStore(OVERRIDE_STORE_NAME, { keyPath: 'job_id' });
                }
            },
        });
    }

    async queueRequest(requestConfig) {
        const db = await this.dbPromise;
        const serializedRequest = {
            url: requestConfig.url,
            method: requestConfig.method,
            data: requestConfig.data,
            params: requestConfig.params,
            headers: requestConfig.headers,
            timestamp: Date.now(),
        };
        await db.add(STORE_NAME, serializedRequest);
        console.log('Request queued for offline sync:', serializedRequest);
    }

    async getQueuedRequests() {
        const db = await this.dbPromise;
        return db.getAll(STORE_NAME);
    }

    async removeRequest(id) {
        const db = await this.dbPromise;
        await db.delete(STORE_NAME, id);
    }

    async cacheResponse(url, data) {
        const db = await this.dbPromise;
        await db.put(CACHE_STORE_NAME, { url, data, timestamp: Date.now() });
    }

    async getCachedResponse(url) {
        const db = await this.dbPromise;
        const cached = await db.get(CACHE_STORE_NAME, url);
        // Optional: Implement cache expiration logic here
        return cached ? cached.data : null;
    }
    
    async clearQueue() {
        const db = await this.dbPromise;
        await db.clear(STORE_NAME);
    }

    // --- Outcome overrides ------------------------------------------------
    // Queued mutations return a 202 but a refetch wipes the optimistic UI —
    // the server still shows the old status until replay lands. Overrides
    // persist the worker's recorded outcome over every refetch until the
    // server confirms it. This was the "marked done, app forgets, re-asks"
    // bug's fix.
    async setOutcomeOverride(jobId, status, extras = {}) {
        const db = await this.dbPromise;
        await db.put(OVERRIDE_STORE_NAME, {
            job_id: jobId,
            status,
            extras,
            ts: Date.now(),
        });
    }

    async getOutcomeOverrides() {
        const db = await this.dbPromise;
        const all = await db.getAll(OVERRIDE_STORE_NAME);
        const map = {};
        for (const o of all) map[o.job_id] = o;
        return map;
    }

    async clearOutcomeOverride(jobId) {
        const db = await this.dbPromise;
        await db.delete(OVERRIDE_STORE_NAME, jobId);
    }
}

export const offlineManager = new OfflineManager();
